import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontalIcon, ArrowUpDownIcon, FilterIcon, XIcon } from 'lucide-react';
import { useHorizontalScroll } from '../lib/hooks/useHorizontalScroll';
import { motion, AnimatePresence } from 'framer-motion';
import { EnhancedProductCard } from '../components/shared/EnhancedProductCard';
import { NowCompactProductCard } from '../components/now/NowCompactProductCard';
import { FilterSidebar } from '../components/category/FilterSidebar';
import { MobileFilterModal } from '../components/category/MobileFilterModal';
import { MobileSortModal } from '../components/category/MobileSortModal';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { Pagination } from '../components/shared/Pagination';
interface CategoryPLPProps {
  categoryName: string;
  categoryDescription: string;
  isNowContext?: boolean;
}
export function CategoryPLP({
  categoryName,
  categoryDescription,
  isNowContext = false
}: CategoryPLPProps) {
  const [searchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const ITEMS_PER_PAGE = 20;
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showMobileSort, setShowMobileSort] = useState(false);
  const [showFab, setShowFab] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    brands: [] as string[],
    priceRange: {
      min: 0,
      max: 5000
    },
    categories: [] as string[],
    needsPrescription: false,
    isExpress: false,
    isGlobal: false,
    offers: [] as string[]
  });
  const [sortBy, setSortBy] = useState('best-match');
  useEffect(() => {
    // Show FAB immediately on mobile
    setShowFab(true);
    const handleScroll = () => {
      // Optional: hide/show based on scroll direction or position if needed
      // For now, keeping it always visible for better UX
      setShowFab(true);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const activeFilters = useMemo(() => {
    const filters: {
      id: string;
      label: string;
      type: string;
    }[] = [];
    selectedFilters.brands.forEach(brand => {
      filters.push({
        id: `brand-${brand}`,
        label: brand,
        type: 'brand'
      });
    });
    selectedFilters.categories.forEach(cat => {
      filters.push({
        id: `cat-${cat}`,
        label: cat,
        type: 'category'
      });
    });
    selectedFilters.offers.forEach(offer => {
      filters.push({
        id: `offer-${offer}`,
        label: offer,
        type: 'offer'
      });
    });
    if (selectedFilters.needsPrescription) {
      filters.push({
        id: 'rx',
        label: 'Prescription Required',
        type: 'rx'
      });
    }
    if (selectedFilters.priceRange.min > 0 || selectedFilters.priceRange.max < 5000) {
      filters.push({
        id: 'price',
        label: `${selectedFilters.priceRange.min} - ${selectedFilters.priceRange.max} SAR`,
        type: 'price'
      });
    }
    return filters;
  }, [selectedFilters]);
  const removeFilter = (filter: {
    id: string;
    label: string;
    type: string;
  }) => {
    if (filter.type === 'brand') {
      setSelectedFilters(prev => ({
        ...prev,
        brands: prev.brands.filter(b => b !== filter.label)
      }));
    } else if (filter.type === 'category') {
      setSelectedFilters(prev => ({
        ...prev,
        categories: prev.categories.filter(c => c !== filter.label)
      }));
    } else if (filter.type === 'offer') {
      setSelectedFilters(prev => ({
        ...prev,
        offers: prev.offers.filter(o => o !== filter.label)
      }));
    } else if (filter.type === 'rx') {
      setSelectedFilters(prev => ({
        ...prev,
        needsPrescription: false
      }));
    } else if (filter.type === 'price') {
      setSelectedFilters(prev => ({
        ...prev,
        priceRange: {
          min: 0,
          max: 5000
        }
      }));
    }
  };
  const clearAllFilters = () => {
    setSelectedFilters({
      brands: [],
      priceRange: {
        min: 0,
        max: 5000
      },
      categories: [],
      needsPrescription: false,
      offers: []
    });
  };
  // Single banner row with 16:9 aspect ratio
  const banners = [{
    img: 'https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=1200&h=400&fit=crop',
    link: '#',
    alt: 'Category Banner 1'
  }, {
    img: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=1200&h=400&fit=crop',
    link: '#',
    alt: 'Category Banner 2'
  }];
  // Mock product data using home page structure
  const products = [{
    id: 1,
    name: 'Bambi Pants Box (Size) 6 X - LARGE * 68',
    brand: 'Bambi',
    price: 113.85,
    originalPrice: 145.0,
    discount: 22,
    rating: 4.8,
    reviewCount: 156,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp',
    isBestSeller: true
  }, {
    id: 2,
    name: 'Fine Baby Junior Jumbo Pack Size 6 Baby Diapers 22 Kg 38 Diapers',
    brand: 'Fine Baby',
    price: 54.51,
    originalPrice: 68.99,
    discount: 21,
    rating: 4.7,
    reviewCount: 203,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 3,
    name: 'Babyjoy Culotte Size 6 Junior Xxl 16+ Kg Giant Pack 50 Diaper Pants',
    brand: 'Babyjoy',
    price: 94.21,
    originalPrice: 120.0,
    discount: 21,
    rating: 4.9,
    reviewCount: 178,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp',
    isNew: true
  }, {
    id: 4,
    name: 'Babyjoy Culotte Size 4 Large Jumbo Pack 8-10 Kg 44 Diaper Pants',
    brand: 'Babyjoy',
    price: 62.81,
    originalPrice: 79.99,
    discount: 21,
    rating: 4.6,
    reviewCount: 142,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 5,
    name: 'Pampers Premium Care Size 5 Junior 11-16 Kg 40 Diapers',
    brand: 'Pampers',
    price: 89.99,
    originalPrice: 110.0,
    discount: 18,
    rating: 4.8,
    reviewCount: 289,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp',
    isBestSeller: true
  }, {
    id: 6,
    name: 'Huggies Ultra Comfort Size 4 Mega Pack 8-14 Kg 66 Diapers',
    brand: 'Huggies',
    price: 78.5,
    originalPrice: 95.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 234,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 7,
    name: 'Pampers Active Baby Size 4 Maxi 9-14 Kg 82 Diapers',
    brand: 'Pampers',
    price: 95.99,
    originalPrice: 115.0,
    discount: 17,
    rating: 4.6,
    reviewCount: 198,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 8,
    name: 'Babyjoy Compressed Size 5 Junior 12-18 Kg 58 Diapers',
    brand: 'Babyjoy',
    price: 72.5,
    originalPrice: 89.99,
    discount: 19,
    rating: 4.5,
    reviewCount: 167,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 9,
    name: 'Fine Baby Smart Lock Size 5 Junior 11-18 Kg 60 Diapers',
    brand: 'Fine Baby',
    price: 68.99,
    originalPrice: 85.0,
    discount: 19,
    rating: 4.4,
    reviewCount: 145,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 10,
    name: 'Huggies Dry Comfort Size 5 Junior 12-22 Kg 44 Diapers',
    brand: 'Huggies',
    price: 82.5,
    originalPrice: 99.99,
    discount: 17,
    rating: 4.7,
    reviewCount: 212,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 11,
    name: 'Pampers Baby Dry Size 3 Midi 6-10 Kg 74 Diapers',
    brand: 'Pampers',
    price: 79.99,
    originalPrice: 95.0,
    discount: 16,
    rating: 4.6,
    reviewCount: 189,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 12,
    name: 'Babyjoy Healthy Skin Size 4 Large 10-18 Kg 62 Diapers',
    brand: 'Babyjoy',
    price: 75.5,
    originalPrice: 92.0,
    discount: 18,
    rating: 4.5,
    reviewCount: 156,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 13,
    name: 'Fine Baby Natural Touch Size 4 Maxi 7-14 Kg 50 Diapers',
    brand: 'Fine Baby',
    price: 58.99,
    originalPrice: 72.0,
    discount: 18,
    rating: 4.4,
    reviewCount: 134,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 14,
    name: 'Huggies Snug & Dry Size 6 Extra Large 16+ Kg 52 Diapers',
    brand: 'Huggies',
    price: 86.5,
    originalPrice: 105.0,
    discount: 18,
    rating: 4.6,
    reviewCount: 178,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 15,
    name: 'Pampers Pants Size 5 Junior 12-17 Kg 48 Pants',
    brand: 'Pampers',
    price: 92.99,
    originalPrice: 112.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 201,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 16,
    name: 'Bambi Nature Size 5 Junior 11-25 Kg 44 Diapers',
    brand: 'Bambi',
    price: 98.5,
    originalPrice: 118.0,
    discount: 17,
    rating: 4.8,
    reviewCount: 167,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 17,
    name: 'Babyjoy Compressed Diamond Pad Size 6 XXL 16+ Kg 40 Diapers',
    brand: 'Babyjoy',
    price: 69.99,
    originalPrice: 85.0,
    discount: 18,
    rating: 4.5,
    reviewCount: 145,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 18,
    name: 'Fine Baby Super Dry Size 3 Medium 4-9 Kg 68 Diapers',
    brand: 'Fine Baby',
    price: 62.5,
    originalPrice: 76.0,
    discount: 18,
    rating: 4.4,
    reviewCount: 123,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 19,
    name: 'Huggies Little Snugglers Size 2 Newborn 3-6 Kg 84 Diapers',
    brand: 'Huggies',
    price: 74.99,
    originalPrice: 90.0,
    discount: 17,
    rating: 4.6,
    reviewCount: 189,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 20,
    name: 'Pampers Swaddlers Size 1 Newborn 2-5 Kg 92 Diapers',
    brand: 'Pampers',
    price: 81.5,
    originalPrice: 98.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 234,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 21,
    name: 'Babyjoy New Born Size 1 Newborn 2-4 Kg 100 Diapers',
    brand: 'Babyjoy',
    price: 65.99,
    originalPrice: 80.0,
    discount: 18,
    rating: 4.5,
    reviewCount: 156,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 22,
    name: 'Fine Baby Diaper Pants Size 5 Junior 12-22 Kg 36 Pants',
    brand: 'Fine Baby',
    price: 56.5,
    originalPrice: 68.0,
    discount: 17,
    rating: 4.4,
    reviewCount: 134,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 23,
    name: 'Huggies Pull-Ups Size 4 Large 8-15 Kg 42 Training Pants',
    brand: 'Huggies',
    price: 77.99,
    originalPrice: 94.0,
    discount: 17,
    rating: 4.6,
    reviewCount: 178,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 24,
    name: 'Pampers Cruisers Size 4 Maxi 9-14 Kg 70 Diapers',
    brand: 'Pampers',
    price: 88.5,
    originalPrice: 106.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 201,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 25,
    name: 'Bambi Premium Care Size 4 Maxi 8-16 Kg 56 Diapers',
    brand: 'Bambi',
    price: 102.99,
    originalPrice: 124.0,
    discount: 17,
    rating: 4.8,
    reviewCount: 167,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 26,
    name: 'Babyjoy Twin Pack Size 5 Junior 12-18 Kg 2x58 Diapers',
    brand: 'Babyjoy',
    price: 135.5,
    originalPrice: 165.0,
    discount: 18,
    rating: 4.6,
    reviewCount: 189,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 27,
    name: 'Fine Baby Mega Pack Size 6 XXL 16+ Kg 54 Diapers',
    brand: 'Fine Baby',
    price: 71.99,
    originalPrice: 87.0,
    discount: 17,
    rating: 4.5,
    reviewCount: 145,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 28,
    name: 'Huggies Natural Care Size 3 Midi 5-9 Kg 78 Diapers',
    brand: 'Huggies',
    price: 83.5,
    originalPrice: 101.0,
    discount: 17,
    rating: 4.6,
    reviewCount: 178,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 29,
    name: 'Pampers Pure Protection Size 2 Mini 4-8 Kg 88 Diapers',
    brand: 'Pampers',
    price: 93.99,
    originalPrice: 113.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 212,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 30,
    name: 'Babyjoy Culotte Mega Pack Size 3 Medium 6-11 Kg 56 Pants',
    brand: 'Babyjoy',
    price: 67.5,
    originalPrice: 82.0,
    discount: 18,
    rating: 4.5,
    reviewCount: 156,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 31,
    name: 'Fine Baby Comfort Size 4 Large 7-14 Kg 62 Diapers',
    brand: 'Fine Baby',
    price: 64.99,
    originalPrice: 78.0,
    discount: 17,
    rating: 4.4,
    reviewCount: 134,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 32,
    name: 'Huggies Overnight Size 5 Junior 12-22 Kg 48 Diapers',
    brand: 'Huggies',
    price: 89.5,
    originalPrice: 108.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 189,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 33,
    name: 'Pampers Splashers Size 4 Maxi 9-15 Kg 24 Swim Pants',
    brand: 'Pampers',
    price: 45.99,
    originalPrice: 55.0,
    discount: 16,
    rating: 4.5,
    reviewCount: 123,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 34,
    name: 'Bambi Sensitive Size 3 Midi 4-9 Kg 72 Diapers',
    brand: 'Bambi',
    price: 96.5,
    originalPrice: 116.0,
    discount: 17,
    rating: 4.6,
    reviewCount: 167,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 35,
    name: 'Babyjoy Super Soft Size 2 Mini 3-6 Kg 96 Diapers',
    brand: 'Babyjoy',
    price: 73.99,
    originalPrice: 90.0,
    discount: 18,
    rating: 4.5,
    reviewCount: 145,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 36,
    name: 'Fine Baby Ultra Thin Size 5 Junior 11-18 Kg 52 Diapers',
    brand: 'Fine Baby',
    price: 66.5,
    originalPrice: 80.0,
    discount: 17,
    rating: 4.4,
    reviewCount: 134,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 37,
    name: 'Huggies Gentle Care Size 1 Newborn 2-5 Kg 90 Diapers',
    brand: 'Huggies',
    price: 78.99,
    originalPrice: 95.0,
    discount: 17,
    rating: 4.6,
    reviewCount: 178,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 38,
    name: 'Pampers Harmonie Size 3 Midi 6-10 Kg 66 Diapers',
    brand: 'Pampers',
    price: 97.5,
    originalPrice: 117.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 201,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 39,
    name: 'Babyjoy Premium Dry Size 4 Large 10-18 Kg 58 Diapers',
    brand: 'Babyjoy',
    price: 71.99,
    originalPrice: 87.0,
    discount: 17,
    rating: 4.5,
    reviewCount: 156,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 40,
    name: 'Fine Baby Value Pack Size 4 Maxi 7-14 Kg 72 Diapers',
    brand: 'Fine Baby',
    price: 69.5,
    originalPrice: 84.0,
    discount: 17,
    rating: 4.4,
    reviewCount: 134,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }];
  // Pagination calculations
  const totalPages = Math.ceil(products.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedProducts = products.slice(startIndex, endIndex);
  return <div className="min-h-screen bg-gray-50 pt-[var(--header-height-mobile)] md:pt-0" data-id="element-6715" data-mp="yoekh7u6vn4d">
      <MobileInternalHeader title={categoryName} data-id="element-6716" data-mp="tmxi17w23slo" />
      {/* Banner Row - 16:9 aspect ratio */}
      <div className="container mx-auto px-4 md:px-8 lg:px-12 py-1 md:py-4 pt-2 md:pt-4" data-id="element-6717" data-mp="pngckegmqvgi">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6718" data-mp="3aol6c0f1h9yc">
          {banners.map((banner, index) => <a key={index} href={banner.link} className="block rounded-xl overflow-hidden bg-gray-300 hover:shadow-md transition-shadow relative" data-id="element-6719" data-mp="35qb6hciddl6e" data-mpi={index}>
              <div className="relative w-full" style={{
            paddingTop: '56.25%'
          }} data-id="element-6720" data-mp="2wns7j3hpfspf">
                <div className="absolute inset-0 flex flex-col justify-center p-6 md:p-8 text-gray-800" data-id="element-6721" data-mp="2mz0k98nl9wku">
                  <span className="text-xs font-bold bg-white text-gray-600 px-3 py-1 rounded-full w-max mb-3" data-id="element-6722" data-mp="2hz5w3cv77l3h">
                    DIMENSIONS
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold mb-2" data-id="element-6723" data-mp="2dw95ayvi8iog">
                    BANNER SIZE
                  </h2>
                  <p className="text-sm md:text-base font-medium mb-3" data-id="element-6724" data-mp="23zcv8opyjohs">
                    50% width (desktop) | 100% width (mobile)
                  </p>
                  <button className="bg-white hover:bg-gray-100 text-gray-700 font-bold py-2 px-4 rounded-md w-max text-sm" data-id="element-6725" data-mp="jhlxwnx8h6ze">
                    Ratio 16:9
                  </button>
                </div>
              </div>
            </a>)}
        </div>
      </div>
      {/* 6 Banner Row - 3:1 aspect ratio */}
      <div className="container mx-auto px-4 md:px-8 lg:px-12 pb-1 md:pb-4" data-id="element-6726" data-mp="xfae3igcvpy4">
        {/* Desktop View - 6 banners in a row */}
        <div className="hidden md:grid md:grid-cols-6 gap-3" data-id="element-6727" data-mp="5pxlejj2oghw">
          {[1, 2, 3, 4, 5, 6].map(index => <a key={index} href="#" className="rounded-lg overflow-hidden relative bg-gray-300 transform transition-transform hover:scale-105" data-id="element-6728" data-mp="9qzml5wjv2mi">
              <div className="w-full h-32" data-id="element-6729" data-mp="3e57j7ffxizjv"></div>
              <div className="absolute inset-0 flex flex-col justify-center p-3 text-gray-800" data-id="element-6730" data-mp="3e4fy4yjykk7u">
                <span className="text-xs font-bold bg-white text-gray-600 px-2 py-0.5 rounded-full w-max mb-2" data-id="element-6731" data-mp="3i5rhwng5ib84">
                  DIMENSIONS
                </span>
                <h3 className="text-sm font-bold mb-1" data-id="element-6732" data-mp="kdhnivu6fad9">1/3 WIDTH BANNER</h3>
                <p className="text-xs font-medium" data-id="element-6733" data-mp="z3ghemep7zmx">ASPECT RATIO 3:1</p>
              </div>
            </a>)}
        </div>
        {/* Mobile View - Horizontal Scroll */}
        <div className="md:hidden flex overflow-x-auto snap-x snap-mandatory hide-scrollbar gap-3 pb-2 -mx-4 px-4" data-id="element-6734" data-mp="6e9jnpn2je2q">
          {[1, 2, 3, 4, 5, 6].map(index => <a key={index} href="#" className="flex-none w-[70vw] snap-center rounded-lg overflow-hidden relative bg-gray-300" data-id="element-6735" data-mp="1h9c1aakm39i">
              <div className="w-full h-32" data-id="element-6736" data-mp="bc1b6ijswy6e"></div>
              <div className="absolute inset-0 flex flex-col justify-center p-4 text-gray-800" data-id="element-6737" data-mp="1qu31xss8rl1c">
                <span className="text-[10px] font-bold bg-white text-gray-600 px-2 py-0.5 rounded-full w-max mb-1.5" data-id="element-6738" data-mp="1mt1dm6zv4te5">
                  DIMENSIONS
                </span>
                <h3 className="text-sm font-bold mb-0.5" data-id="element-6739" data-mp="1uedbgftvid5t">1/3 WIDTH BANNER</h3>
                <p className="text-xs font-medium" data-id="element-6740" data-mp="1zcycghgy90vb">ASPECT RATIO 3:1</p>
              </div>
            </a>)}
        </div>
      </div>
      {/* Filters + Sort + Product Grid */}
      <div className="container mx-auto px-4 md:px-8 lg:px-12 pb-12 md:pb-12 pb-32" data-id="element-6741" data-mp="1pe8rugxo52bd">
        {/* Mobile Results Count Bar */}
        <div className="md:hidden py-3 px-4 flex items-center justify-between -mx-4 mb-2 bg-white border-b border-gray-100" data-id="element-6742" data-mp="2d3jw48e89eu9">
          <div className="text-sm font-bold text-gray-900" data-id="element-6743" data-mp="23cyq3zw8581b">
            {products.length} products
          </div>
          <div className="text-xs text-gray-500 font-medium" data-id="element-6744" data-mp="28bs5xyyzhb4n">
            Results: {startIndex + 1}-{Math.min(endIndex, products.length)}
          </div>
        </div>

        {/* Active Filter Chips Row (Mobile Only) */}
        {activeFilters.length > 0 && <div className="md:hidden flex overflow-x-auto hide-scrollbar gap-2 mb-4 -mx-4 px-4 pb-1" data-id="element-6745" data-mp="30ufkswndztxa">
            {activeFilters.map(filter => <button key={filter.id} onClick={() => removeFilter(filter)} className="flex-none flex items-center gap-1 px-3 py-1.5 bg-blue-50 border border-blue-100 text-[#0066CC] rounded-full text-sm font-medium whitespace-nowrap" data-id="element-6746" data-mp="184oo70rzvoyy">
                {filter.label}
                <XIcon className="w-3.5 h-3.5" data-id="element-6747" data-mp="1vtzgruz6yd20" />
              </button>)}
            <button onClick={clearAllFilters} className="flex-none px-3 py-1.5 text-gray-500 text-sm font-medium underline whitespace-nowrap" data-id="element-6748" data-mp="1qwh6o19ftrsf">
              Clear all
            </button>
          </div>}

        {/* Desktop Layout: Sidebar + Grid */}
        <div className="flex gap-6" data-id="element-6749" data-mp="2em1yl9nkjzdu">
          {/* Desktop Filter Sidebar */}
          <div className="hidden md:block w-64 lg:w-72 flex-shrink-0" data-id="element-6750" data-mp="29ts7hqapwt9z">
            <div className="sticky top-24" data-id="element-6751" data-mp="2i2xv7uiwuzwm">
              <FilterSidebar selectedFilters={selectedFilters} onFilterChange={setSelectedFilters} data-id="element-6752" data-mp="2i3zfmpltgyr0" />
            </div>
          </div>
          {/* Product Grid */}
          <div className="flex-1" data-id="element-6753" data-mp="2r2ulg0ky86ts">
            {/* Results count and sort */}
            <div className="flex items-center justify-between mb-6 bg-white p-4 rounded-lg" data-id="element-6754" data-mp="2w2z4nyd8rfnp">
              <p className="text-sm text-gray-600" data-id="element-6755" data-mp="312lj2b4t31y3">
                Results:{' '}
                <span className="font-medium text-gray-900" data-id="element-6756" data-mp="3535dv81wpth2">
                  {startIndex + 1}-{Math.min(endIndex, products.length)}
                </span>{' '}
                of{' '}
                <span className="font-medium text-gray-900" data-id="element-6757" data-mp="3et73w5dtllym">
                  {products.length}
                </span>
              </p>
              {/* Desktop sort dropdown */}
              <div className="hidden md:flex items-center gap-2" data-id="element-6758" data-mp="3jq72jv8cbmfp">
                <span className="text-sm text-gray-600" data-id="element-6759" data-mp="3os66p7bwmndp">Sort By</span>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0066CC]" data-id="element-6760" data-mp="2z2pmm7shxv7r">
                  <option value="best-match" data-id="element-6761" data-mp="342bz2f0i37fi">Recommended</option>
                  <option value="top-rated" data-id="element-6762" data-mp="340r6guxqxyka">Top Rated</option>
                  <option value="price-low-high" data-id="element-6763" data-mp="3ht78bsewvqjm">Price: Low to High</option>
                  <option value="price-high-low" data-id="element-6764" data-mp="3cunnjvc20ksu">Price: High to Low</option>
                  <option value="newest" data-id="element-6765" data-mp="3cwqd6e9aobx1">Newest</option>
                </select>
              </div>
            </div>
            {/* Product grid switches to the compact NOW 120 card only when entered from NOW. */}
            <div className={`grid gap-2 md:gap-4 mb-8 ${isNowContext ? 'grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6' : 'grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`} data-id="element-6766" data-mp="3mqyzgk7qf8mr">
              {paginatedProducts.map(product => isNowContext ? <NowCompactProductCard key={product.id} product={product} data-id="element-6767" data-mp="7yung730je6c" /> : <EnhancedProductCard key={product.id} product={product} data-id="element-6768" data-mp="cvbbz2e4jujy" />)}
            </div>
            {/* Pagination */}
            <Pagination currentPage={currentPage} totalPages={totalPages} data-id="element-6769" data-mp="361k9nn70dwd" />
          </div>
        </div>
      </div>

      {/* Floating Filter & Sort FAB (Mobile Only) */}
      <AnimatePresence data-id="element-6770" data-mp="3lacxa7irtab9">
        {showFab && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} className="md:hidden fixed bottom-[72px] left-0 right-0 mx-auto w-max z-40 flex items-center bg-[#0066CC] text-white rounded-xl shadow-2xl overflow-hidden" data-id="element-6771" data-mp="3pdre30ej9upr">
            <button onClick={() => setShowMobileSort(true)} className="flex items-center justify-center gap-1.5 px-3.5 py-2.5 border-r border-white/20 active:bg-white/10 transition-colors" data-id="element-6772" data-mp="1ej3zofdk2xey">
              <ArrowUpDownIcon className="w-3.5 h-3.5" data-id="element-6773" data-mp="3or63gk2xh8ru" />
              <span className="text-[11px] font-bold uppercase tracking-wide" data-id="element-6774" data-mp="3opl7qr4vnjse">
                Sort
              </span>
            </button>
            <button onClick={() => setShowMobileFilters(true)} className="flex items-center justify-center gap-1.5 px-3.5 py-2.5 active:bg-white/10 transition-colors relative" data-id="element-6775" data-mp="3et02q5suy8ok">
              <FilterIcon className="w-3.5 h-3.5" data-id="element-6776" data-mp="35rs4tou979v0" />
              <span className="text-[11px] font-bold uppercase tracking-wide" data-id="element-6777" data-mp="35uo3jfdxe2ki">
                Filter
              </span>
              {activeFilters.length > 0 && <span className="absolute top-1.5 right-2 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center border border-[#0066CC]" data-id="element-6778" data-mp="13zvq66cshjw0">
                  {activeFilters.length}
                </span>}
            </button>
          </motion.div>}
      </AnimatePresence>

      {/* Mobile Filter Modal */}
      <MobileFilterModal isOpen={showMobileFilters} onClose={() => setShowMobileFilters(false)} selectedFilters={selectedFilters} onFilterChange={setSelectedFilters} data-id="element-6779" data-mp="1gawn0195n86z" />
      {/* Mobile Sort Modal */}
      <MobileSortModal isOpen={showMobileSort} onClose={() => setShowMobileSort(false)} sortBy={sortBy} onSortChange={setSortBy} data-id="element-6780" data-mp="nobw11wlcy3y" />
    </div>;
}